/**
 * constants.js
 */

export const CSRF_HEADER_NAME = "x-csrf-token";
export const PTAPI_VERSION_HEADER_NAME = "x-ptapi-version";
export const PTUSER_HEADER_NAME = 'x-ptuser';
export const CSRF_STORAGE_NAME = "csrfToken";

// maximum request size allowed by API server (configurable in web.config)
export const MAX_REQUEST_SIZE_BYTES = 30 * 1024 * 1024;

export const NOTIFICATION_ANIMATION_DURATION = 500;

export const RECORD_QUERY_PAGE_BUFFER = 1;

export const TOUR_BEACON_VIEW_LIMIT = 10;

export const LATE_COMPLETION_DAYS = 14;

export const ONBOARDING_RECORDS_LIMIT = 30;

export const ARREST_DEMOGRAPHIC_START_DATE = new Date('2024-03-19T00:00:00Z');

let Types = {

  // ========================================
  // SHARED TYPES
  // These types should match similar definitions on the server.

  // User roles
  Role: {
    HANDLER: 1,
    SUPERVISOR: 2,
    TRAINER: 3,
    MANAGER: 4
  },

  // Core record types
  Record: {
    EVENT: 0,
    EXERCISE: 1,
    ANSWER: 2,
    DEPLOYMENT: 3,
    CLASS: 4,
    OTHER: 5,
    VET_VISIT: 6,
  },

  // Record group types
  RecordGroup: {
    ALL: 0, // used internally by filter form
    TRAINING: 1,
    DEPLOYMENT: 2,
    CLASS: 3,
    VET_VISIT: 5
  },

  // Event permissions roles
  PermissionsRole: {
    MEMBER: 0,
    LEADER: 1
  },

  // Dog work modes
  WorkMode: {
    ALL: 0,
    DETECTION: 1,
    PATROL: 2,
  },

  FulfillmentState: {
    DogDeployed: 1,
    DogNotDeployed: 2,
    CancelledEnroute: 3,
  },

  // Patrol types
  Patrol: {
    SCENARIO: 100,
    OBEDIENCE: 0,
    AGILITY: 1,
    APPREHENSION: 2,
    HUMAN_SEARCH: 3,
    EVIDENCE_SEARCH: 4,
    BUILDING_SEARCH: 5,
    TRACKING: 6,
    OTHER: 7,
    NONSEARCH: 8,

    Training: {
      OBEDIENCE: 0,
      AGILITY: 1,
      APPREHENSION: 2,
      HUMAN_SEARCH: 3,
      EVIDENCE_SEARCH: 4,
      BUILDING_SEARCH: 5,
      TRACKING: 6,
      OTHER: 7,
    },

    Deployment: {
      HUMAN_SEARCH: 3,
      EVIDENCE_SEARCH: 4,
      BUILDING_SEARCH: 5,
      TRACKING: 6,
      NONSEARCH: 8,
    }
  },

  // Detection environments
  Environment: {
    VEHICLE: 1,
    PERSON: 2,
    ROOM: 3,
    OPEN_AREA: 4,
    LUGGAGE: 5,
    PARCEL: 6,
    LOCKER: 7,
  },

  // Detection Odors Types
  OdorPrimary: {
    PROOFING: 1,
    ALCOHOL: 2,
    ARSON: 3,
    CADAVER: 4,
    CONCEALED_HUMANS: 5,
    CURRENCY: 6,
    DRUGS: 7,
    DRUG_PARAPHERNALIA: 8,
    ELECTRONICS: 9,
    EXPLOSIVES: 10,
    FIREARMS: 11,
    WILDLIFE: 12,
  },

  // Detection Vehicle Types
  Vehicle: {
    PASSSENGER_CAR: 485,
    BUS: 486,
    SUBWAY_CAR: 487,
    AIRPLANE: 488,
    BOAT: 489,
    VAN: 490,
    TRACTOR_TRAILER: 491,
    MOTORCYCLE: 492,
    SUV: 25113,
    TRUCK: 25114,
    OTHER: 493
  },

  // Date range types
  DateRange: {
    ALL_TIME: 0,
    THIS_MONTH: 1,
    LAST_MONTH: 2,
    LAST_30_DAYS: 3,
    LAST_90_DAYS: 4,
    THIS_YEAR: 5,
    LAST_YEAR: 6,
    CUSTOM: 7,
  },

  // Reviewed status types
  ReviewStatus: {
    NOT_REVIEWED: 0,
    ACCEPTED: 1,
    REJECTED: 2
  },

  MemberRequest: {
    FROM_USER: 1,
    TO_USER: 2
  },

  NotificationEventChannel: {
    UNKNOWN: 0,
    INVITATION: 1,
    UPCOMING_TRAINING: 2,
    EXERCISE_READY_FOR_COMPLETION: 3,
    MANAGER_FEEDBACK: 4,
    RECORD_UPDATE: 5,
    EXERCISE_READY_FOR_COMMENTS: 6,
    GENERAL_UPDATE: 7,
    VACCINATION_DUE: 8,
  },

  NotificationDestination: {
    UNKNOWN: 0,
    NOTIFICATION_DRAWER: 1 << 0,
    PERSISTENT_TOAST: 1 << 1,
    QUICK_TOAST: 1 << 2,
    MOBILE_NOTIFICATION: 1 << 3,
    EMAIL: 1 << 4,
  },


  // ========================================
  // FRONT-END TYPES
  // These types are only used by the client.

  // theme types
  Theme: {
    LIGHT: 'light',
    DARK: 'dark',
  },

  // filter modes
  FilterMode: {
    FILTER: 'filter',
    SAVED_SEARCH: 'saved-search',
    QUICK_LINK: 'quick-link',
    DATE: 'date',
  },

  // quick link types
  QuickLinkType: {
    INCOMPLETE: 'incomplete',
    LATE_COMPLETION: 'late-completion',
    REJECTED: 'rejected',
    NOT_REVIEWED: 'not-reviewed',
    OUTDATED: 'outdated',
    OVERDUE_VAC: 'overdue-vac',
  },

  // Filter criteria field
  FilterCriteriaField: {
    DROPDOWN: 0,
    DROPDOWN2: 1,
    TEXT: 2,
    TAG: 3,
  },

  // event attendance types
  Attendance: {
    ATTENDING: 'attending',
    NOT_ATTENDING: 'not-attending',
    UNDECIDED: 'undecided'
  },

  // General Deployment constants
  Deployment: {
    ETHNICITIES_SEXES_NA_TEXT: 'N/A',
    MAX_NUM_ARRESTS: 50,
  },

  // Deployment section types
  DeploymentSection: {
    GENERAL: 0,
    DETECTION: 1,
    PATROL: 2,
    SUMMARY: 3,
    BUTTONSCROLL: '1O',
  },

}; 

// Secondarily-Defined Constants
Types = {
  ...Types,

  // SHARED TYPES
  NotificationDestinationGroupValues: {
    SYSTEM: Types.NotificationDestination.NOTIFICATION_DRAWER | Types.NotificationDestination.PERSISTENT_TOAST,
    EMAIL: Types.NotificationDestination.EMAIL,
    MOBILE_APP: Types.NotificationDestination.MOBILE_NOTIFICATION,
  },

  // FRONT-END TYPES
}

Object.freeze( Types );
export { Types };

// Returns whether or not the bitmask integer contains the specified property id value
export const isBitfieldContainingBitmask = (bitfield, bitmask) => {
  return (bitfield & bitmask) !== 0;
}

// Add accessor methods to the objects in Types
( () => {

  // Internal function to convert type's integer id to translated string
  function translateVal(t, val) {
    return t(`types.${this.baseTypeName.toCamelCase()}.${val}`);
  };

  // Returns an array of this type's property values
  function values(exclude) {
    return this
      .allValues
      .filter(val => !exclude || !exclude.includes(val));
  };

  // Returns a string array of translated names for this type's properties. This
  // requires that the translation file for the associated types use a consistent
  // matching format. ex: translating Types.Record.DEPLOYMENT (val: 3) to a
  // suitable string name requires a translation file with a types.record.3
  // entry of "Deployment".
  function tNames(t, exclude) {
    return this.values(exclude).map(id => this.translateVal(t, id));
  };

  // Returns an array of objects with this type's translated property names and values
  function entries(t, exclude) {
    return this.values(exclude).map(id => ({ id, name: this.translateVal(t, id) }));
  };

  // Returns the id of the specified property name (key)
  function getId(key) {
    if (!key) return null;
    
    return Object.keys(this)
      .findIndex(k => k === key.toUpperCase());
  };

  // Return the translated name of the specified property id value
  function getName(t, val) {
    return this.translateVal(t, val);
  };

  const attachMethods = (obj, baseTypeName) => {
    obj.allValues = Object.values(obj).filter(v => typeof v !== 'object');
    obj.baseTypeName = baseTypeName;
    obj.translateVal = translateVal;
    obj.values = values;
    obj.tNames = tNames;
    obj.entries = entries;
    obj.getId = getId;
    obj.getName = getName;
  }

  const findAndAttachToObjects = (obj, baseTypeName) => {
    for (let key in obj) {
      if (key !== 'allValues' && typeof obj[key] === 'object') {
        attachMethods(obj[key], baseTypeName || key);
        findAndAttachToObjects(obj[key], baseTypeName || key);
      }
    }
  }

  // Attach meta methods to each type recursively.
  // Note that root base type name is used for any recursively defined type lists.
  findAndAttachToObjects(Types, null);

})();